import React, { useContext, useEffect, useState } from "react";
import { Box, Flex } from "theme-ui";

import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import UserContext from "context/User";
import { themevals } from "theme/themevals";
import { Pill } from "containers/Leads/List";

import OpportunityDrawer, { DuePill, STAGE_LABEL } from "./Drawer";
import { createMoFromProject, getMoForProject, type MoRow } from "api/MaintenanceOpportunities";

/**
 * The project record's Maintenance card (phase 1): on a COMPLETED project,
 * shows the site's maintenance opportunity - when the top-up is due, where
 * it stands, the next step - and opens the same drawer as the queue. If
 * the nightly pass skipped the site (on a plan, a newer project, a managed
 * installer, or not run yet), "Create opportunity" adds it.
 *
 * Self-hiding: company users with maintenanceOppView only, completed
 * projects only (status stepNumber >= 5, the proposal generator's own
 * "completed" test), and nothing at all while the switch is off (the
 * lookup 403s and the card renders null).
 */
const MaintenanceProjectCard: React.FC<{ project: any }> = ({ project }) => {
  const { user } = useContext(UserContext) as any;
  const toast = useToast();
  const [state, setState] = useState<{ opportunity: MoRow | null; reason: string | null } | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const allowed = user?.accessLevel === "isCompany" && (user?.role?.permissions || []).includes("maintenanceOppView");
  const completed = Number(project?.status?.stepNumber ?? 0) >= 5;
  const canEdit = (user?.role?.permissions || []).includes("maintenanceOppEdit");

  const load = () =>
    getMoForProject(project.id).then((res: any) => {
      if (res?.error) return setState(null);
      setState(res);
    });

  useEffect(() => {
    if (allowed && completed && project?.id) load();
  }, [project?.id, allowed, completed]); // eslint-disable-line

  if (!allowed || !completed || !state || state.reason === "no_location") return null;
  const o = state.opportunity;
  const st = o ? STAGE_LABEL[o.stage] ?? STAGE_LABEL.candidate : null;

  return (
    <Box sx={{ border: "1px solid #E4E7EB", borderRadius: "8px", padding: "12px 14px", marginBottom: "16px", background: "#FFFFFF" }}>
      <Flex sx={{ justifyContent: "space-between", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
        <Box>
          <div style={{ fontFamily: "Rubik", fontWeight: 600, fontSize: 14 }}>Opportunity</div>
          <div style={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>
            {o
              ? o.endCustomer?.name
                ? `Owner: ${o.lead?.name ?? o.client?.name ?? o.endCustomer.name}`
                : "Owner not identified yet"
              : "This site is not in Opportunities."}
          </div>
        </Box>
        <Flex sx={{ gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
          {o && st && <Pill bg={st.bg} fg={st.fg}>{st.label}</Pill>}
          {o && <DuePill dueInDays={o.dueInDays} dueAt={o.dueAt} />}
          {o ? (
            <BasicButton outline scheme="secondary" type="button" keepText onClick={() => setOpen(true)}>Open</BasicButton>
          ) : (
            canEdit && (
              <BasicButton
                type="button"
                keepText
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  const res: any = await createMoFromProject(project.id);
                  setBusy(false);
                  if (res?.error) return toast.add(res.error, "var(--theme-ui-colors-danger)");
                  toast.add("Added to Opportunities.", "var(--theme-ui-colors-success)");
                  setState({ opportunity: res, reason: null });
                  setOpen(true);
                }}
              >
                {busy ? "Adding…" : "Create opportunity"}
              </BasicButton>
            )
          )}
        </Flex>
      </Flex>
      {open && o && <OpportunityDrawer id={o.id} onClose={() => setOpen(false)} onChanged={load} />}
    </Box>
  );
};

export default MaintenanceProjectCard;
